import { type ViewColumn } from 'common/types/viewColumn';
import { type ExtraOptions, type TableColumn } from '../types';
import { map, each, isNil, keyBy, isObject } from 'lodash';
import { type Row } from './type';
import {
  agGridDataFormatter,
  isEmailType,
} from 'common/visualizations/views/agGridReact/helpers/TableColumnFormatting';
import { type Column, type GridApi, type ICellRendererParams } from 'ag-grid-community';
import { isUrlColumnType } from 'common/visualizations/helpers/CalendarHelper';
import { SoQLType } from 'common/types/soql';
import { DIV_ZERO_SENTINEL } from 'common/authoring_workflow/components/modals/AdvancedTotalingModal/helpers/formulaHelpers';
import { isTableAdvancedTotalingForExportsEnabled } from 'common/visualizations/helpers/VifSelectors';
import { I18n } from 'common/i18n';
import { getFieldName } from 'common/visualizations/views/agGridReact/helpers/getFieldName';

interface FormatRowDataArgs {
  rowData: Row[];
  tableColumns: TableColumn[];
  viewColumns: ViewColumn[];
  gridApi: GridApi;
  extraOptions: ExtraOptions;
}

// Tried doing it in the print preview worker. But the DataTypeFormatter requires
// jQuery and i18n. So for now, formatting the table cell content in the main thread.
// Later: Probably, we can create a generic DataTypeFormatter that takes
// sanitizeHTML/currencySymbol as inputs.
// - original DataTypeFormatter will use jquery/I18n for sanitizeHTML/currencySymbol
// - web worker DataTypeFormatter where we will pass a new sanitizeHTML and pass
//  currencySymbol to webWorker from mainThread
export const formatRows = ({
  rowData,
  tableColumns,
  viewColumns,
  extraOptions,
  gridApi,
}: FormatRowDataArgs) => {
  const { dataset, nonStandardAggregations, columnFormats } = extraOptions;
  const allDisplayedColumns: Column[] = gridApi.getAllDisplayedColumns();
  const rowGroupCols: Column[] = gridApi.getRowGroupColumns();
  const columnsById = keyBy(allDisplayedColumns.concat(rowGroupCols), (col) => col.getColId().toString());
  const fieldToViewColumn = keyBy(viewColumns, (viewColumn) => viewColumn.fieldName);

  return map(rowData, (rowDatum: Row) => {
    const newRowDatum: Row = { ...rowDatum };

    each(tableColumns, (tableColumn) => {
      const value = rowDatum[tableColumn.field];
      const columnDef = columnsById[tableColumn.field]?.getColDef();
      const newColumnParams = {
        ...columnDef,
        colDef: columnDef,
        value,
        valueFormatted: value,
        api: gridApi,
      } as ICellRendererParams;
      // pivot columns use compound field names like "Skiing@@gold"; resolve back to the
      // underlying soql column so we can look up its type/formatting metadata
      const resolvedFieldName = getFieldName(newColumnParams) ?? tableColumn.field;
      const viewColumn = fieldToViewColumn[resolvedFieldName];
      if (!viewColumn) {
        // nothing to format against; leave the raw value in the row rather than crashing
        return;
      }
      const isTextColumn = viewColumn.renderTypeName === SoQLType.SoQLTextT;
      const isUrlColumn = isUrlColumnType(resolvedFieldName, viewColumns);
      const isEmailColumn = isEmailType(resolvedFieldName, viewColumns);
      const isCountAgg = columnDef.aggFunc === 'count';
      const isAggValue = ['subTotal', 'total', 'header'].includes(rowDatum._rowType);
      const dataTypesToReturnTrueValues = [SoQLType.SoQLBooleanT, SoQLType.SoQLFloatingTimestampT];

      if (isCountAgg && dataTypesToReturnTrueValues.includes(viewColumn.dataTypeName) && isAggValue) {
        // The PDF incorrectly renders the aggregation values (COUNT) as formatted values
        // when the data types are either checkboxes or calendar dates.
        return value;
      }

      if (isTableAdvancedTotalingForExportsEnabled() && value == DIV_ZERO_SENTINEL) {
        newRowDatum[tableColumn.field] = I18n.t('error_in_table', {
          scope: 'shared.components.advanced_totaling',
        });
        newRowDatum._divZeroFields = [...(newRowDatum._divZeroFields ?? []), tableColumn.field];
        return;
      }

      if (!isNil(value)) {
        const formattedValue =
          isUrlColumn || isEmailColumn
            ? formatUrlValue(value)
            : agGridDataFormatter(
                dataset.domain,
                dataset.datasetUid,
                viewColumns,
                nonStandardAggregations,
                newColumnParams,
                columnFormats,
                false,
                true,
              );
        newRowDatum[tableColumn.field!] = isTextColumn ? decodeHtmlEntities(formattedValue) : formattedValue;
      }
    });

    return newRowDatum;
  });
};

interface URLValue {
  url: string;
}
const formatUrlValue = (value: URLValue | string) => {
  return isObject(value) ? value.url : value;
};

const decodeHtmlEntities = (text: string) => {
  const div = document.createElement('div');
  div.innerHTML = text;
  return div.textContent || div.innerText;
};
